import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp,
  Query,
  CollectionReference
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SearchHistory } from '../models/SearchHistory';

// Collection name in Firestore
const COLLECTION_NAME = 'searchHistory';

/**
 * Add a new search history entry to Firestore
 * @param historyData Search history data to add
 * @returns The ID of the newly created search history entry
 */
export const addSearchHistory = async (historyData: Omit<SearchHistory, 'id' | 'createdAt'>): Promise<string> => {
  try {
    const dataWithTimestamps = {
      ...historyData,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), dataWithTimestamps);
    return docRef.id;
  } catch (error) {
    console.error('Error adding search history:', error);
    throw error;
  }
};

/**
 * Get all search history entries from Firestore
 * @param limit Optional limit to the number of results
 * @returns Array of search history entries
 */
export const getAllSearchHistory = async (limitCount?: number): Promise<SearchHistory[]> => {
  try {
    let q: Query | CollectionReference = collection(db, COLLECTION_NAME);
    
    // Apply ordering and limit if specified
    if (limitCount) {
      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
    } else {
      q = query(q, orderBy('createdAt', 'desc'));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to Date objects
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date()
      } as SearchHistory;
    });
  } catch (error) {
    console.error('Error getting search history:', error);
    throw error;
  }
};

/**
 * Update a search history entry in Firestore
 * @param id Search history ID
 * @param historyData Search history data to update
 * @returns Promise that resolves when the update is complete
 */
export const updateSearchHistory = async (id: string, historyData: Partial<Omit<SearchHistory, 'id' | 'createdAt'>>): Promise<void> => {
  try {
    const historyRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(historyRef, historyData);
  } catch (error) {
    console.error('Error updating search history:', error);
    throw error;
  }
};

/**
 * Delete a search history entry from Firestore
 * @param id Search history ID
 * @returns Promise that resolves when the deletion is complete
 */
export const deleteSearchHistory = async (id: string): Promise<void> => {
  try {
    const historyRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(historyRef);
  } catch (error) {
    console.error('Error deleting search history:', error);
    throw error;
  }
};

/**
 * Get search history entries by a specific field value
 * @param field Field name to query
 * @param value Field value to match
 * @param limit Optional limit to the number of results
 * @returns Array of search history entries matching the query
 */
export const getSearchHistoryByField = async (field: keyof SearchHistory, value: any, limitCount?: number): Promise<SearchHistory[]> => {
  try {
    let q: Query | CollectionReference = query(collection(db, COLLECTION_NAME), where(field, '==', value), orderBy('createdAt', 'desc'));
    
    // Apply limit if specified
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to Date objects
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date()
      } as SearchHistory;
    });
  } catch (error) {
    console.error(`Error getting search history by ${field}:`, error);
    throw error;
  }
};

/**
 * Get search history entries for a specific user
 * @param userId User ID
 * @param limit Optional limit to the number of results
 * @returns Array of search history entries for the user
 */
export const getUserSearchHistory = async (userId: string, limitCount?: number): Promise<SearchHistory[]> => {
  // Since userId is not a field in SearchHistory, we'll need to use a different approach
  // For now, we'll just return all search history
  return getSearchHistory();
};

export const getSearchHistory = async (): Promise<SearchHistory[]> => {
    try {
        const historyQuery = query(collection(db, COLLECTION_NAME), orderBy('timestamp', 'desc'));
        const snapshot = await getDocs(historyQuery);
        return snapshot.docs.map(doc => {
            const data = doc.data();
            
            // Handle both new filters format and old direct properties
            const filters = {
                location: data.filters?.location || 
                         (data.location ? [data.location] : []),
                industries: data.filters?.industries || 
                          (data.industry ? [data.industry] : [])
            };

            // Ensure results is always an array
            const results = Array.isArray(data.results) ? data.results : [];

            return {
                id: doc.id,
                query: data.query || '',
                searchType: data.searchType || 'business',
                resultsCount: data.resultsCount || results.length || 0,
                filters,
                results,
                timestamp: data.timestamp?.toDate() || new Date(),
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date()
            } as SearchHistory;
        });
    } catch (error) {
        console.error('Error fetching search history:', error);
        throw error;
    }
};

export const getSearchHistoryById = async (id: string): Promise<SearchHistory> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
            throw new Error('Search history not found');
        }

        return {
            id: docSnap.id,
            ...docSnap.data(),
            timestamp: docSnap.data().timestamp.toDate(),
        } as SearchHistory;
    } catch (error) {
        console.error('Error fetching search history by ID:', error);
        throw error;
    }
}; 